import { useCallback, useRef, useState, type ReactElement, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

export interface CarouselPosition {
  trackRef: RefObject<HTMLDivElement | null>;
  /** Attach to the track's `onScroll`. */
  onScroll: () => void;
  active: number;
  goTo: (index: number) => void;
}

/**
 * Which slide of a `.carousel` track is in view, and a way to bring one into
 * view. Slide offsets are measured from the first slide, so the track's own
 * padding and gap need no constants here.
 */
export function useCarouselPosition(): CarouselPosition {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const offsetOf = (track: HTMLElement, slide: Element): number => {
    const first = track.firstElementChild;

    return first === null
      ? 0
      : slide.getBoundingClientRect().left - first.getBoundingClientRect().left;
  };

  const onScroll = useCallback((): void => {
    const track = trackRef.current;

    if (track === null || track.children.length === 0) {
      return;
    }

    // At the far end the last slides can't reach the snap point, so the end
    // of the track counts as the last slide rather than the nearest one.
    if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) {
      setActive(track.children.length - 1);
      return;
    }

    const offsets = Array.from(track.children, (slide) => offsetOf(track, slide));
    let nearest = 0;

    offsets.forEach((offset, index) => {
      if (
        Math.abs(offset - track.scrollLeft) < Math.abs((offsets[nearest] ?? 0) - track.scrollLeft)
      ) {
        nearest = index;
      }
    });

    setActive(nearest);
  }, []);

  const goTo = useCallback((index: number): void => {
    const track = trackRef.current;
    const slide = track?.children[index];

    if (track === null || slide === undefined) {
      return;
    }

    track.scrollTo({ left: offsetOf(track, slide), behavior: 'smooth' });
  }, []);

  return { trackRef, onScroll, active, goTo };
}

export function CarouselDots({
  count,
  active,
  onSelect,
}: {
  count: number;
  active: number;
  onSelect: (index: number) => void;
}): ReactElement | null {
  const { t } = useTranslation();

  if (count < 2) {
    return null;
  }

  return (
    <div className="mt-1 flex items-center justify-center">
      {Array.from({ length: count }, (_, index) => (
        <button
          key={index}
          type="button"
          aria-label={t('common.slide', { number: index + 1 })}
          aria-current={index === active}
          onClick={() => {
            onSelect(index);
          }}
          // The dot stays small; the padding gives a finger something to hit.
          className="group px-[3px] py-2"
        >
          <span
            className={`block h-1.5 rounded-full transition-all ${
              index === active ? 'w-5 bg-fog-300' : 'w-1.5 bg-ink-600 group-hover:bg-fog-500'
            }`}
          />
        </button>
      ))}
    </div>
  );
}
